import EventType from 'domains/models/event_type';
import {
  schemaEventTypeCalendarCreateBody,
  schemaEventTypeCalendarUpdate,
} from 'presentations/api/schema/event_type_calendar';
import {
  schemaEventTypeLocationItem,
  schemaEventTypeLocationListItem,
  schemaEventTypeLocationUpdateBody,
} from '../event_type_location';
import { schemaEventTypeSettingPublicItem } from '../event_type_setting';
import { MailSenderTypeEnum } from '../event_type_setting/enum';
import { schemaEventTypeUserListItem } from '../event_type_user';
import { RoleEnum, UserTypeEnum } from '../event_type_user/enum';
import { schemaEventTypeServiceListItem } from '../event_type_service';
import { formatToISO } from 'infras/datetime';
import { schemaCancelPolicyItem } from '../cancel_policy';
import { schemaTermItem } from '../term';
import {
  schemaEventTypeLinkItem,
  schemaEventTypeLinkUpdateBody,
} from '../event_type_link';
import { schemaEventTypeResourceSetListItem } from '../event_type_resource_set';
import { z } from 'zod';
import { schemaWorkflowItem } from '../workflow';
import { schemaTransparencyKeywordItem } from '../transparency_keyword';
import { schemaEventTypeNotificationItem } from '../event_type_notification';
import { schemaFormMinItem } from '../form';
import { PlanIdEnum, PlanVersionEnum } from '../subscription/enum';
import {
  AttendTypeEnum,
  AvailableUserTypeEnum,
  CalendarTypeEnum,
  CalendarViewTypeEnum,
  ConfirmationTypeEnum,
  DisplayTypeEnum,
  EventTypeTypeEnum,
  EventTypeVersionEnum,
  HostAssignTypeEnum,
  HostOperationTypeEnum,
  PeriodTypeEnum,
  RescheduleTypeEnum,
  ReservationMethodEnum,
  RoundRobinAllocateTypeEnum,
  StatusEnum,
  ViewerTimeZoneTypeEnum,
} from './enum';
import { schemaSendMailUserListItem } from '../send_mail_user';

const WeekDay = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6),
]);
const schemaStartWeekDay = z.union([WeekDay, z.literal(99)]);

export type EventTypeUserCreate = z.infer<typeof schemaEventTypeUserCreate>;
export const schemaEventTypeUserCreate = z.object({
  userId: z.string(),
  role: RoleEnum,
  userType: UserTypeEnum,
  priority: z.number(),
});

export type EventTypeUserUpdate = z.infer<typeof schemaEventTypeUserUpdate>;
export const schemaEventTypeUserUpdate = z.object({
  userId: z.string(),
  role: RoleEnum,
  userType: UserTypeEnum,
});

export type EventTypeServiceCreate = z.infer<
  typeof schemaEventTypeServiceCreate
>;
export const schemaEventTypeServiceCreate = z.object({
  serviceId: z.number(),
  sort: z.number(),
  isRequired: z.boolean(),
});

export type EventTypeResourceSetUpdate = z.infer<
  typeof schemaEventTypeResourceSetUpdate
>;
export const schemaEventTypeResourceSetUpdate = z.object({
  resourceSetId: z.number(),
  sort: z.number(),
  priority: z.number(),
});

export type EventFormCreateBody = z.infer<typeof schemaEventFormCreateBody>;
export const schemaEventFormCreateBody = z.object({
  name: z.string().nullable(),
  workflows: z.array(schemaWorkflowItem),
});

export type EventFormUpdateBody = z.infer<typeof schemaEventFormUpdateBody>;
export const schemaEventFormUpdateBody = z.object({
  name: z.string().nullable(),
  workflows: z.array(schemaWorkflowItem),
});

export type EventTypeTransparencyKeywordCreateBody = z.infer<
  typeof schemaEventTypeTransparencyKeywordCreateBody
>;
export const schemaEventTypeTransparencyKeywordCreateBody = z.object({
  transparencyKeywords: z.array(schemaTransparencyKeywordItem),
});

export type EventTypeTransparencyKeywordUpdateBody = z.infer<
  typeof schemaEventTypeTransparencyKeywordUpdateBody
>;
export const schemaEventTypeTransparencyKeywordUpdateBody = z.object({
  transparencyKeywords: z.array(schemaTransparencyKeywordItem),
});

export type EventTypeSettingCreateBody = z.infer<
  typeof schemaEventTypeSettingCreateBody
>;
export const schemaEventTypeSettingCreateBody = z.object({
  isCompanyRequired: z.boolean().optional(),
  isCompanyHidden: z.boolean().optional(),
  isCcEmailsRequired: z.boolean().optional(),
  isCcEmailsHidden: z.boolean().optional(),
  isMessageRequired: z.boolean().optional(),
  isMessageHidden: z.boolean().optional(),
  isShareHostMail: z.boolean().optional(),
  mailSenderType: MailSenderTypeEnum,
});

export type EventTypeCoverImageUpdateResult = z.infer<
  typeof schemaEventTypeCoverImageUpdateResult
>;
export const schemaEventTypeCoverImageUpdateResult = z.object({
  coverImage: z.string().nullable(),
});

export type EventTypeCreateBody = z.infer<typeof schemaEventTypeCreateBody>;
export const schemaEventTypeCreateBody = z.object({
  teamId: z.number(),
  name: z.string(),
  coverImage: z.string().nullable(),
  duration: z.number(),
  intervalMinutes: z.number(),
  description: z.string(),
  hostOperationType: HostOperationTypeEnum,
  eventTypeType: EventTypeTypeEnum,
  displayType: DisplayTypeEnum,
  internalNote: z.string().nullable(),
  slug: z.string(),
  selectedCalendarViewTypes: z.string(),
  calendarViewType: CalendarViewTypeEnum,
});
export type EventTypeFormCreateBody = z.infer<
  typeof schemaEventTypeFormCreateBody
>;
export const schemaEventTypeFormCreateBody = z.object({
  eventType: schemaEventTypeCreateBody,
  location: schemaEventTypeLocationItem,
  eventTypeUsers: z.array(schemaEventTypeUserCreate),
  eventTypeCalendar: schemaEventTypeCalendarCreateBody.nullable(),
  eventTypeNotification: schemaEventTypeNotificationItem.nullable(),
  eventTypeSetting: schemaEventTypeSettingCreateBody.nullable(),
  form: schemaEventFormCreateBody.nullable(),
  eventTypeTransparencyKeyword: schemaEventTypeTransparencyKeywordCreateBody.nullable(),
});

export type CustomEventTypeFormCreateBody = z.infer<
  typeof schemaCustomEventTypeFormCreateBody
>;
export const schemaCustomEventTypeFormCreateBody = z.object({
  eventType: schemaEventTypeCreateBody,
  location: schemaEventTypeLocationItem,
  eventTypeUsers: z.array(schemaEventTypeUserCreate),
  eventTypeCalendar: schemaEventTypeCalendarCreateBody.nullable(),
  eventTypeNotification: schemaEventTypeNotificationItem.nullable(),
  eventTypeSetting: z.object({
    isCalendarInvite: z.boolean(),
  }),
  form: schemaEventFormCreateBody.nullable(),
  eventTypeTransparencyKeyword: schemaEventTypeTransparencyKeywordCreateBody.nullable(),
  isReserveSchedule: z.boolean(),
});

// API側で使用
export type EventTypeUpdate = z.infer<typeof schemaEventTypeUpdate>;
export const schemaEventTypeUpdate = z
  .object({
    uid: z.string(),
    name: z.string(),
    projectId: z.number().nullable(),
    duration: z.number(),
    description: z.string(),
    confirmText: z.string(),
    displayType: DisplayTypeEnum,
    maxCapacity: z.number().nullable(),
    googleAnalyticsId: z.string().nullable(),
    confirmationType: ConfirmationTypeEnum,
    confirmRedirectUrl: z.string().nullable(),
    calendarViewType: CalendarViewTypeEnum,
    selectedCalendarViewTypes: z.string().nullable(),
    coverImage: z.string().nullable(),
    intervalMinutes: z.number(),
    availableUserType: AvailableUserTypeEnum,
    hostAssignType: HostAssignTypeEnum,
    rescheduleType: RescheduleTypeEnum,
    color: z.string(),
    status: StatusEnum,
    isRevealGuestUser: z.boolean(),
    cancelConfirmationType: ConfirmationTypeEnum,
    cancelConfirmRedirectUrl: z.string().nullable(),
    rescheduleConfirmationType: ConfirmationTypeEnum,
    rescheduleConfirmRedirectUrl: z.string().nullable(),
  })
  .partial();

export type EventTypeUpdateBody = z.infer<typeof schemaEventTypeUpdateBody>;
export const schemaEventTypeUpdateBody = z
  .object({
    eventType: schemaEventTypeUpdate,
    location: schemaEventTypeLocationUpdateBody,
    link: schemaEventTypeLinkUpdateBody,
    eventTypeUsers: z.array(schemaEventTypeUserUpdate),
    eventTypeServices: z.array(schemaEventTypeServiceCreate),
    eventTypeResourceSets: z.array(schemaEventTypeResourceSetUpdate),
    eventTypeCancelPolicyId: z.number().nullable(),
    eventTypeTermId: z.number().nullable(),
    calendar: schemaEventTypeCalendarUpdate,
    form: schemaEventFormUpdateBody,
    eventTypeTransparencyKeyword: schemaEventTypeTransparencyKeywordUpdateBody,
  })
  .partial();

export type EventTypeServiceRestrictUsageBody = z.infer<
  typeof schemaEventTypeServiceRestrictUsageBody
>;
export const schemaEventTypeServiceRestrictUsageBody = z.object({
  email: z.string(),
  date: z.string().optional(),
});

export type EventTypeRestrictServiceUsageItem = z.infer<
  typeof schemaEventTypeRestrictServiceUsageItem
>;
export const schemaEventTypeRestrictServiceUsageItem = z.object({
  serviceId: z.number(),
  name: z.string(),
  isEnableUserMaxUsesCount: z.boolean(),
  userMaxUsesCount: z.number(),
  userCurrentUsesCount: z.number(),
  isEnableUserMaxMonthlyUsesCount: z.boolean(),
  userMaxMonthlyUsesCount: z.number().nullable(),
  userCurrentMonthlyUsesCount: z.number(),
  isEnableUserMaxWeeklyUsesCount: z.boolean(),
  userMaxWeeklyUsesCount: z.number().nullable(),
  userCurrentWeeklyUsesCount: z.number(),
  isEnableUserMaxDailyUsesCount: z.boolean(),
  userMaxDailyUsesCount: z.number().nullable(),
  userCurrentDailyUsesCount: z.number(),
});

export type EventTypeServiceRestrictUsageResponse = z.infer<
  typeof schemaEventTypeServiceRestrictUsageResponse
>;
export const schemaEventTypeServiceRestrictUsageResponse = z.object({
  usages: z.array(schemaEventTypeRestrictServiceUsageItem),
});

// フロント側の各フォームで使用
/** EventTypeBasicForm */
export type EventTypeBasicFormUpdate = z.infer<
  typeof schemaEventTypeBasicFormUpdate
>;
export const schemaEventTypeBasicFormUpdate = z
  .object({
    uid: z.string(),
    name: z.string(),
    duration: z.number(),
    intervalMinutes: z.number(),
    description: z.string(),
    hostOperationType: HostOperationTypeEnum,
    internalNote: z.string().nullable(),
    slug: z.string(),
  })
  .partial();

export type EventTypeBasicFormUpdateBody = z.infer<
  typeof schemaEventTypeBasicFormUpdateBody
>;
export const schemaEventTypeBasicFormUpdateBody = z
  .object({
    eventType: schemaEventTypeBasicFormUpdate,
    location: schemaEventTypeLocationUpdateBody,
    eventTypeResourceSets: z.array(schemaEventTypeResourceSetUpdate),
  })
  .partial();

/** EventTypeTeamSchedulingForm */
export type EventTypeTeamSchedulingFormUpdate = z.infer<
  typeof schemaEventTypeTeamSchedulingFormUpdate
>;
export const schemaEventTypeTeamSchedulingFormUpdate = z
  .object({
    projectId: z.number().nullable(),
    maxCapacity: z.number().nullable(),
    isRevealGuestUser: z.boolean(),
    availableUserType: AvailableUserTypeEnum,
    hostAssignType: HostAssignTypeEnum,
    roundRobinAllocateType: RoundRobinAllocateTypeEnum,
    rescheduleType: RescheduleTypeEnum,
    isDisplayRemainingCapacity: z.boolean(),
    isDisplayFullCapacity: z.boolean(),
  })
  .partial();

export type EventTypeTeamSchedulingFormUpdateBody = z.infer<
  typeof schemaEventTypeTeamSchedulingFormUpdateBody
>;
export const schemaEventTypeTeamSchedulingFormUpdateBody = z
  .object({
    eventType: schemaEventTypeTeamSchedulingFormUpdate,
    eventTypeUsers: z.array(schemaEventTypeUserCreate),
    eventTypeTransparencyKeyword: schemaEventTypeTransparencyKeywordUpdateBody,
  })
  .partial();

/** EventTypeConfirmationPageForm */
export type EventTypeConfirmationPageFormUpdate = z.infer<
  typeof schemaEventTypeConfirmationPageFormUpdate
>;
export const schemaEventTypeConfirmationPageFormUpdate = z
  .object({
    confirmText: z.string(),
    confirmationType: ConfirmationTypeEnum,
    confirmRedirectUrl: z.string().nullable(),
    cancelConfirmationType: ConfirmationTypeEnum,
    cancelConfirmRedirectUrl: z.string().nullable(),
    rescheduleConfirmationType: ConfirmationTypeEnum,
    rescheduleConfirmRedirectUrl: z.string().nullable(),
  })
  .partial();

export type EventTypeConfirmationPageFormUpdateBody = z.infer<
  typeof schemaEventTypeConfirmationPageFormUpdateBody
>;
export const schemaEventTypeConfirmationPageFormUpdateBody = z.object({
  eventType: schemaEventTypeConfirmationPageFormUpdate.optional(),
});

/** EventTypeNotificationForm */
export type EventTypeNotificationFormUpdateBody = z.infer<
  typeof schemaEventTypeNotificationFormUpdateBody
>;
export const schemaEventTypeNotificationFormUpdateBody = z.object({
  eventType: z
    .object({
      displayType: DisplayTypeEnum.optional(),
    })
    .optional(),
});

/** EventTypeFormForm */
export type EventTypeFormFormUpdateBody = z.infer<
  typeof schemaEventTypeFormFormUpdateBody
>;
export const schemaEventTypeFormFormUpdateBody = z.object({
  form: schemaEventFormUpdateBody.optional(),
});

/** EventTypeServicePaymentForm */
export type EventTypeServicePaymentFormUpdateBody = z.infer<
  typeof schemaEventTypeServicePaymentFormUpdateBody
>;
export const schemaEventTypeServicePaymentFormUpdateBody = z
  .object({
    eventTypeServices: z.array(schemaEventTypeServiceCreate),
    eventTypeCancelPolicyId: z.number().nullable(),
    eventTypeTermId: z.number().nullable(),
  })
  .partial();

/** EventTypeIntegrationForm */
export type EventTypeIntegrationFormUpdateBody = z.infer<
  typeof schemaEventTypeIntegrationFormUpdateBody
>;
export const schemaEventTypeIntegrationFormUpdateBody = z.object({
  eventType: z
    .object({
      googleAnalyticsId: z.string().nullable().optional(),
    })
    .optional(),
});

/** EventTypeDesignForm */
export type EventTypeDesignFormUpdate = z.infer<
  typeof schemaEventTypeDesignFormUpdate
>;
export const schemaEventTypeDesignFormUpdate = z
  .object({
    coverImage: z.string().nullable(),
    calendarViewType: CalendarViewTypeEnum,
    selectedCalendarViewTypes: z.string().nullable(),
    color: z.string(),
    startWeekDay: z.number(),
    displayWeekDay: z.string(),
  })
  .partial();

export type EventTypeDesignFormUpdateBody = z.infer<
  typeof schemaEventTypeDesignFormUpdateBody
>;
export const schemaEventTypeDesignFormUpdateBody = z
  .object({
    eventType: schemaEventTypeDesignFormUpdate,
    link: schemaEventTypeLinkUpdateBody,
  })
  .partial();

/** 有効無効のスイッチ用 */
export type EventTypeStatusUpdateBody = z.infer<
  typeof schemaEventTypeStatusUpdateBody
>;
export const schemaEventTypeStatusUpdateBody = z.object({
  eventType: z
    .object({
      status: StatusEnum.optional(),
    })
    .optional(),
});

export type EventTypeCreateResult = z.infer<typeof schemaEventTypeCreateResult>;
export const schemaEventTypeCreateResult = z.object({
  id: z.number(),
});

export type EventTypeItem = z.infer<typeof schemaEventTypeItem>;
export const schemaEventTypeItem = z.object({
  id: z.number(),
  uid: z.string(),
  teamId: z.number(),
  name: z.string(),
  eventTypeType: EventTypeTypeEnum,
  projectId: z.number().nullable(),
  coverImage: z.string().nullable(),
  duration: z.number(),
  description: z.string(),
  displayType: DisplayTypeEnum,
  intervalMinutes: z.number(),
  maxCapacity: z.number().nullable(),
  periodType: PeriodTypeEnum,
  periodDuration: z.number().nullable(),
  isLimitGracePeriod: z.boolean(),
  gracePeriod: z.number(),
  gracePeriodPoint: z.number(),
  gracePeriodUnit: z.number(),
  periodStart: z.string().nullable(),
  periodEnd: z.string().nullable(),
  bufferBefore: z.number().nullable(),
  bufferAfter: z.number().nullable(),
  googleAnalyticsId: z.string().nullable(),
  location: schemaEventTypeLocationItem.nullable(),
  link: schemaEventTypeLinkItem.nullable(),
  users: z.array(schemaEventTypeUserListItem),
  services: z.array(schemaEventTypeServiceListItem),
  cancelPolicy: schemaCancelPolicyItem.nullable(),
  term: schemaTermItem.nullable(),
  timeZone: z.string(),
  confirmText: z.string(),
  availableUserType: AvailableUserTypeEnum,
  calendarViewType: CalendarViewTypeEnum,
  selectedCalendarViewTypes: z.string().nullable(),
  hostAssignType: HostAssignTypeEnum,
  roundRobinAllocateType: RoundRobinAllocateTypeEnum,
  resourceSets: z.array(schemaEventTypeResourceSetListItem),
  hostOperationType: HostOperationTypeEnum,
  rescheduleType: RescheduleTypeEnum,
  confirmRedirectUrl: z.string().nullable(),
  confirmationType: ConfirmationTypeEnum,
  rescheduleConfirmRedirectUrl: z.string().nullable(),
  rescheduleConfirmationType: ConfirmationTypeEnum,
  cancelConfirmRedirectUrl: z.string().nullable(),
  cancelConfirmationType: ConfirmationTypeEnum,
  form: schemaFormMinItem.nullable(),
  color: z.string(),
  status: StatusEnum,
  calendarType: CalendarTypeEnum,
  eventTypeCalendarTemplateId: z.number().nullable(),
  isRevealGuestUser: z.boolean(),
  reservationMethod: ReservationMethodEnum,
  startWeekDay: schemaStartWeekDay,
  displayWeekDay: z.string(),
  viewerTimeZoneType: ViewerTimeZoneTypeEnum,
  isDisplayRemainingCapacity: z.boolean(),
  isDisplayFullCapacity: z.boolean(),
  slug: z.string(),
  version: EventTypeVersionEnum,
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type SharedEventTypeItem = z.infer<typeof schemaSharedEventTypeItem>;
export const schemaSharedEventTypeItem = schemaEventTypeItem.extend({
  isReserved: z.boolean(),
  setting: schemaEventTypeSettingPublicItem,
  planId: PlanIdEnum,
  planVersion: PlanVersionEnum,
  stripeAccountId: z.string().nullable(),
  isInputTokushoho: z.boolean(),
  isShowPromotion: z.boolean(),
  isShowHeaderTeamImage: z.boolean(),
  isNoIndex: z.boolean(),
  metaPixelId: z.string().nullable(),
  customTag: z.string().nullable(),
  sendUser: schemaSendMailUserListItem.nullable(),
});

export type EventTypeDetailItem = z.infer<typeof schemaEventTypeDetailItem>;
export const schemaEventTypeDetailItem = schemaEventTypeItem.extend({
  transparencyKeywords: z.array(schemaTransparencyKeywordItem),
  internalNote: z.string().nullable(),
});

export type SharedEventTypeListItem = z.infer<
  typeof schemaSharedEventTypeListItem
>;
export const schemaSharedEventTypeListItem = z.object({
  id: z.number(),
  uid: z.string(),
  slug: z.string(),
  eventTypeType: EventTypeTypeEnum,
  projectId: z.number().nullable(),
  name: z.string(),
  duration: z.number(),
  description: z.string(),
  displayType: DisplayTypeEnum,
  coverImage: z.string().nullable(),
  color: z.string(),
  location: schemaEventTypeLocationListItem.nullable(),
  users: z.array(schemaEventTypeUserListItem),
  status: StatusEnum,
  teamId: z.number(),
  calendarType: CalendarTypeEnum,
  eventTypeCalendarTemplateId: z.number().nullable(),
  isRevealGuestUser: z.boolean(),
  reservationMethod: ReservationMethodEnum,
});

export type EventTypeSmallListItem = z.infer<
  typeof schemaEventTypeSmallListItem
>;
export const schemaEventTypeSmallListItem = z.object({
  id: z.number(),
  uid: z.string(),
  slug: z.string(),
  eventTypeType: EventTypeTypeEnum,
  projectId: z.number().nullable(),
  name: z.string(),
  duration: z.number(),
  description: z.string(),
  displayType: DisplayTypeEnum,
  coverImage: z.string().nullable(),
  color: z.string(),
  status: StatusEnum,
  teamId: z.number(),
  calendarType: CalendarTypeEnum,
  eventTypeCalendarTemplateId: z.number().nullable(),
  isRevealGuestUser: z.boolean(),
  hostOperationType: HostOperationTypeEnum,
  reservationMethod: ReservationMethodEnum,
  internalNote: z.string().nullable(),
});

export type EventTypeListItem = z.infer<typeof schemaEventTypeListItem>;
export const schemaEventTypeListItem = schemaEventTypeSmallListItem.extend({
  location: schemaEventTypeLocationListItem.nullable(),
  users: z.array(schemaEventTypeUserListItem),
  timeZone: z.string(),
});

export type PendingEventEventTypeListItem = z.infer<
  typeof schemaPendingEventEventTypeListItem
>;
export const schemaPendingEventEventTypeListItem = schemaEventTypeListItem.extend(
  {
    attendType: AttendTypeEnum,
  },
);

export const toListItem = (eventType: EventType) => {
  const listItem: EventTypeSmallListItem = {
    id: eventType.id!,
    uid: eventType.uid,
    slug: eventType.slug,
    teamId: eventType.teamId,
    eventTypeType: eventType.eventTypeType,
    projectId: eventType.projectId,
    name: eventType.name,
    description: eventType.description,
    coverImage: eventType.coverImage,
    color: eventType.color,
    duration: eventType.duration,
    displayType: eventType.displayType,
    status: eventType.status,
    calendarType: eventType.calendarType,
    eventTypeCalendarTemplateId: eventType.eventTypeCalendarTemplateId,
    isRevealGuestUser: eventType.isRevealGuestUser,
    reservationMethod: eventType.reservationMethod,
    hostOperationType: eventType.hostOperationType,
    internalNote: eventType.internalNote,
  };
  return listItem;
};

export const toItem = (eventType: EventType) => {
  return {
    id: eventType.id!,
    uid: eventType.uid,
    teamId: eventType.teamId,
    eventTypeType: eventType.eventTypeType,
    projectId: eventType.projectId,
    name: eventType.name,
    description: eventType.description,
    duration: eventType.duration,
    displayType: eventType.displayType,
    coverImage: eventType.coverImage,
    maxCapacity: eventType.maxCapacity,
    periodType: eventType.periodType,
    periodDuration: eventType.periodDuration,
    periodStart: eventType.periodStart,
    periodEnd: eventType.periodEnd,
    bufferBefore: eventType.bufferBefore,
    bufferAfter: eventType.bufferAfter,
    googleAnalyticsId: eventType.googleAnalyticsId,
    intervalMinutes: eventType.intervalMinutes,
    timeZone: eventType.timeZone,
    confirmText: eventType.confirmText,
    isLimitGracePeriod: eventType.isLimitGracePeriod,
    gracePeriod: eventType.gracePeriod,
    gracePeriodPoint: eventType.gracePeriodPoint,
    gracePeriodUnit: eventType.gracePeriodUnit,
    availableUserType: eventType.availableUserType,
    calendarViewType: eventType.calendarViewType,
    selectedCalendarViewTypes: eventType.selectedCalendarViewTypes,
    hostAssignType: eventType.hostAssignType,
    roundRobinAllocateType: eventType.roundRobinAllocateType,
    hostOperationType: eventType.hostOperationType,
    rescheduleType: eventType.rescheduleType,
    confirmationType: eventType.confirmationType,
    confirmRedirectUrl: eventType.confirmRedirectUrl,
    rescheduleConfirmRedirectUrl: eventType.rescheduleConfirmRedirectUrl,
    rescheduleConfirmationType: eventType.rescheduleConfirmationType,
    cancelConfirmRedirectUrl: eventType.cancelConfirmRedirectUrl,
    cancelConfirmationType: eventType.cancelConfirmationType,
    color: eventType.color,
    status: eventType.status,
    calendarType: eventType.calendarType,
    eventTypeCalendarTemplateId: eventType.eventTypeCalendarTemplateId,
    isRevealGuestUser: eventType.isRevealGuestUser,
    reservationMethod: eventType.reservationMethod,
    startWeekDay: eventType.startWeekDay,
    displayWeekDay: eventType.displayWeekDay,
    viewerTimeZoneType: eventType.viewerTimeZoneType,
    isDisplayRemainingCapacity: eventType.isDisplayRemainingCapacity,
    isDisplayFullCapacity: eventType.isDisplayFullCapacity,
    slug: eventType.slug,
    version: eventType.version,
    createdAt: formatToISO(eventType.createdAt),
    updatedAt: formatToISO(eventType.updatedAt),
  };
};

export type ServiceDetailListEventTypeItem = z.infer<
  typeof schemaServiceDetailListEventTypeItem
>;
export const schemaServiceDetailListEventTypeItem = z.object({
  id: z.number(),
  uid: z.string(),
  name: z.string(),
  users: z.array(schemaEventTypeUserListItem),
});

export type EventTypeConnectionListItem = z.infer<
  typeof schemaEventTypeConnectionListItem
>;
export const schemaEventTypeConnectionListItem = z.object({
  id: z.number(),
  uid: z.string(),
  name: z.string(),
  users: z.array(schemaEventTypeUserListItem),
  integration: z.object({
    isSalesforce: z.boolean(),
  }),
});
